import React from "react";
import {
  FeatherCreditCard,
  FeatherEdit3,
  FeatherFileText,
  FeatherReceipt,
} from "@subframe/core";
import { useAccountantStatusSummary } from "./useAccountantStatusSummary";

type Props = {
  year: number;
  month: number;
};

function SummarySkeleton() {
  const skeletonStyle: React.CSSProperties = {
    backgroundImage:
      "linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(229,231,235,0.8) 50%, rgba(255,255,255,0.4) 100%)",
    backgroundSize: "200% 100%",
    animation: "skeletonFade 1.6s ease-in-out infinite",
  };

  return (
    <>
      <style>{`
        @keyframes skeletonFade {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm"
        >
          <div
            className="h-6 w-32 rounded bg-neutral-100"
            style={skeletonStyle}
          />
          <div
            className="h-10 w-40 rounded bg-neutral-100"
            style={skeletonStyle}
          />
          <div
            className="h-4 w-48 rounded bg-neutral-100"
            style={skeletonStyle}
          />
        </div>
      ))}
    </>
  );
}

export function AccountantStatusSummaryCards({ year, month }: Props) {
  const { summary, loading, error } = useAccountantStatusSummary(year, month);

  return (
    <div className="flex w-full flex-wrap items-start gap-4">
      {loading ? (
        <SummarySkeleton />
      ) : error ? (
        <div className="flex w-full rounded-md border border-solid border-neutral-border bg-default-background px-6 py-4">
          <span className="text-body font-body text-error-600">{error}</span>
        </div>
      ) : (
        <>
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full items-center justify-between">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Vendor Bills
              </span>
              <FeatherFileText className="text-body font-body text-brand-600" />
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {summary?.vendorBills.processed ?? 0} processed
              </span>
              <span className="text-body font-body text-warning-600">
                {summary?.vendorBills.pending ?? 0} pending
              </span>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              Bills recorded and ready in your books.
            </span>
          </div>

          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full items-center justify-between">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Customer Invoices
              </span>
              <FeatherReceipt className="text-body font-body text-success-600" />
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {summary?.customerInvoices.processed ?? 0} processed
              </span>
              <span className="text-body font-body text-warning-600">
                {summary?.customerInvoices.pending ?? 0} pending
              </span>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              Invoices created and updated for this period.
            </span>
          </div>

          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full items-center justify-between">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Bank Transactions
              </span>
              <FeatherCreditCard className="text-body font-body text-brand-600" />
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {summary?.bankTransactions.categorized ?? 0} categorized
              </span>
              <span className="text-body font-body text-warning-600">
                {summary?.bankTransactions.needsReview ?? 0} need review
              </span>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              Fetched from bank feeds and categorized.
            </span>
          </div>

          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full items-center justify-between">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Journal Entries
              </span>
              <FeatherEdit3 className="text-body font-body text-neutral-600" />
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {summary?.otherEntries.posted ?? 0} posted
              </span>
              <span className="text-body font-body text-warning-600">
                {summary?.otherEntries.pending ?? 0} pending
              </span>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              Additional accounting entries for this month.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
